import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PermissionConst } from '@app/constants/permission-const';
import { AuthorizationService } from "@app/services/authorization.service";
import { AuthenticationService } from "@app/services/authentication.service";
import { NgZone } from '@angular/core';
import { Router, IsActiveMatchOptions, NavigationEnd } from "@angular/router";
import { filter } from 'rxjs/operators';
declare var bootstrap: any;

export interface MenuItem {
  label: string;
  icon?: string;
  route?: string;
  children?: MenuItem[];
}

@Component({
    selector: 'app-left-menu',
    templateUrl: './left-menu.component.html',
    styleUrls: ['./left-menu.component.css'],
    standalone: false
})
export class LeftMenuComponent implements OnInit {

  @Input() isNavbarMinimized: boolean;
  @Output() isNavbarMinimizedChange = new EventEmitter<boolean>();

  isAdmin: boolean = false;
  isSuperAdmin: boolean = false;
  allowViewExchangeRateList: boolean = false;
  userManagementIcon = 'assets/img/admin/customer_managment.svg';
  userManagementIconActive = 'assets/img/admin/customer_managment_a.svg';
  accessIcon = 'assets/img/admin/role.svg';
  accessIconActive = 'assets/img/admin/role_a.svg';
  kycIcon = 'assets/img/menu/kyc.svg';
  kycIconActive = 'assets/img/menu/kyc-a.svg';
  currenciesIcon = 'assets/img/admin/exchange_rate_a.svg';
  currenciesIconActive = 'assets/img/admin/exchange_rate_a.svg';
  branchesIconActive = 'assets/img/menu/branches_a.svg';
  branchesIcon = 'assets/img/menu/branches.svg';
  loanTemplIcon = 'assets/img/admin/loan_product.svg';
  loanTemplIconActive = 'assets/img/admin/loan_product_a.svg';
  commissionSettingIcon = 'assets/img/admin/commision.svg';
  commissionSettingIconActive = 'assets/img/admin/commision_a.svg';
  settingsIcon = 'assets/img/admin/settings.svg';
  settingsIconActive = 'assets/img/admin/settings_a.svg';
  productsActive = 'assets/img/menu/products_a.svg';
  products = 'assets/img/menu/products.svg';
  userRolesList = [];
  private readonly adminProductPermissions = [
    'loanTemplate:view',
    'saveagreementTemplate:view',
    'deposit:template:view',
    'operation:commission:view'
  ];
  private readonly adminNotesPermissions = [
    'loan:note:view',
    'saving:note:view',
    'customer:note:view',
    'collateral-note:view',
    'guarantee-note:view',
    'deposit:note:view'
  ];
  private readonly adminSettingsPermissions = [
    'banking:day:change',
    'currency:view',
    'job:view',
    'accountType:view',
    'holiday:view',
    'cash:desk:view',
    'company:view',
    'printingforms:view',
    'notification:view',
    'push:notification:view'
  ];

  minimizeMenuEndpoints = [
    'accounts',
    'chart-of-accounts',
    'jobs',
    'loan-provisioning',
    'transaction-history',
    'off-balance-transaction-history', 
    'summary-of-loans',
    'summary-of-loan-operations',
    'summary-of-deposits',
    'summary-of-deposit-operations',
    'summary-of-savings',
    'summary-of-saving-operations',
    'summary-of-collaterals',
    'summary-of-collateral-operations',
    'summary-of-guarantee',
    'summary-of-guarantee-operations',
    'balance-sheet',
    'turnover',
    'off-balance-turnover',
    'imported-data',
    'operational-issues'
  ];

  dropdownReportsOpen = false;

  constructor(
    private authorizationService: AuthorizationService,
    private authenticationService: AuthenticationService,
    private ngZone: NgZone,
    public router: Router) { }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      const dropdownToggles = document.querySelectorAll('[data-bs-toggle="dropdown"]');

      dropdownToggles.forEach((dropdownToggle: Element) => {
        const bsDropdown = new bootstrap.Dropdown(dropdownToggle);

        dropdownToggle.addEventListener('click', (event) => {
          event.preventDefault();
          bsDropdown.toggle();
        });
      });
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.authorizationService.isAdmin();
    this.isSuperAdmin = this.authorizationService.isSuperAdmin();
    this.allowViewExchangeRateList = this.authorizationService.hasPermission(PermissionConst.EXCHANGERATE.LIST);
    if (this.authenticationService.getUserRoles()) {
      this.userRolesList = JSON.parse(this.authenticationService.getUserRoles())
    }
    document.addEventListener('click', this.hasSubClickListener);

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (this.minimizeMenuEndpoints.some(str => event.urlAfterRedirects.includes(str))) {
        this.isNavbarMinimized = true;
        this.isNavbarMinimizedChange.emit(this.isNavbarMinimized);
      }
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.hasSubClickListener);
  }
  
  hasSubClickListener = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (target.closest('.has-sub')) {
      this.isNavbarMinimized = false;
      this.isNavbarMinimizedChange.emit(this.isNavbarMinimized);
      
      event.preventDefault();
      const menuElement = document.getElementById('left-side-menu');
      if (menuElement) {
        menuElement.classList.remove('hk-nav-toggle');
      }
    }
  };

  hasPermissionSection(sectionName: string) {
  return this.authorizationService.hasPermissionSection(sectionName);
}

hasPermission(permissionSlug) {
  return this.authorizationService.hasPermission(permissionSlug);
}

  hasAnyPermission(permissionSlugs: string[]) {
    return permissionSlugs.some(permissionSlug => this.hasPermission(permissionSlug));
  }

  hasAdminProductsPermission() {
    return this.hasAnyPermission(this.adminProductPermissions);
  }

  hasAdminNotesPermission() {
    return this.hasAnyPermission(this.adminNotesPermissions);
  }

  hasAdminSettingsPermission() {
    return this.hasAnyPermission(this.adminSettingsPermissions) || this.hasAdminNotesPermission() || this.hasLinkedAccountsPermission();
  }

  hasAdminPermissions() {
    return this.isAdmin && !this.isSuperAdmin;
  }

  hasAdminRiskClassificationPermission() {
    return this.hasPermission('risk:classification:view');
  }

  hasAdminNotificationsPermission() {
    return this.hasPermission('notification:view');
  }

  hasLinkedAccountsPermission() {
    return this.hasPermission('loan:linked:account:view') || this.hasPermission('saving:linked:account:view')
    || this.hasPermission('collateral:linked:account:view') || this.hasPermission('guarantee:linked:account:view')
    || this.hasPermission('currency:exchange:linked:account:view') || this.hasPermission('commission:linked:account:view')
    || this.hasPermission('deposit:linked:account:view');
  }

  toggleReportsDropdown(event: Event): void {
    this.dropdownReportsOpen = !this.dropdownReportsOpen;
  }

  get isProductsActive(): boolean {
    return this.isLinkActive('/user/loans') || this.isLinkActive('/admin/loans') ||
        this.isLinkActive('/user/deposits') || this.isLinkActive('/admin/deposits') ||
        this.isLinkActive('/user/deposit-products') || this.isLinkActive('/admin/deposit-products') ||
        this.isLinkActive('/user/saving-products') || this.isLinkActive('/admin/saving-products') ||
        this.isLinkActive('/user/commission-settings')|| this.isLinkActive('/admin/commission-settings');
  }

  isLinkActive(url: string): boolean {
    const exactMatch: IsActiveMatchOptions = {
      paths: 'exact',
      queryParams: 'exact',
      matrixParams: 'ignored',
      fragment: 'ignored'
    };
    return this.router.isActive(url, exactMatch);
  }

  get isOrdersActive(): boolean {
    const urls = [
      '/user/memorial-orders',
      '/user/cash-in-orders',
      '/user/cash-out-orders',
      '/user/off-balance-debit-orders',
      '/user/off-balance-credit-orders',
      '/user/currency-exchange-orders'
    ];

    return urls.some(url => this.router.isActive(url, {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored'
    }));
  }

  get isSettingsActive(): boolean {
    const urls = [
      '/user/operations-day',
      '/user/jobs',
      '/user/chart-of-accounts',
      '/user/notes',
      '/user/linked-accounts',
      '/user/printing-forms',
      '/user/notifications',
      '/user/loan-provisioning'
    ];

    return urls.some(url => this.router.isActive(url, {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored'
    }));
  }

  get isReportsActive(): boolean {
    const urls = [
      '/user/transaction-history',
      '/user/turnover',
      '/user/balance-sheet',
      '/user/off-balance-turnover',
      '/user/off-balance-transaction-history',
      '/user/summary-of-loans',
      '/user/summary-of-loan-operations',
      '/user/summary-of-deposits',
      '/user/summary-of-deposit-operations',
      '/user/summary-of-savings',
      '/user/summary-of-saving-operations',
      '/user/summary-of-collaterals',
      '/user/summary-of-collateral-operations',
      '/user/summary-of-guarantee',
      '/user/summary-of-guarantee-operations'
    ];

    return urls.some(url =>
        this.router.isActive(url, {
          paths: 'exact',
          queryParams: 'ignored',
          matrixParams: 'ignored',
          fragment: 'ignored'
        })
    );
  }

}
